import React from 'react';

import { constant, invariant, tagged } from '@code-expert/prelude';
import { useForceUpdateWithDep } from '/imports/ui/hooks/useForceUpdate';

const setIntervalWithIndex = (fn: (id: NodeJS.Timeout) => void, interval: number) => {
  const id = setInterval(() => void fn(id), interval);
  return id;
};

type State = tagged.Tagged<'running', NodeJS.Timeout> | tagged.Tagged<'paused'>;

const State = tagged.build<State>();

type Action = tagged.Tagged<'set', [(id: NodeJS.Timeout) => void, number]> | tagged.Tagged<'clear'>;

const Action = tagged.build<Action>();

const paused = constant(State.wide.paused());
const update: (action: Action) => (current: State) => State = Action.fold({
  set: ([fn, interval]) =>
    State.fold({
      running: (id) => {
        clearInterval(id);
        return State.running(setIntervalWithIndex(fn, interval));
      },
      paused: () => State.running(setIntervalWithIndex(fn, interval)),
    }),
  clear: () =>
    State.fold({
      running: (id) => {
        clearInterval(id);
        return paused();
      },
      paused,
    }),
});

/**
 * Wraps {@link setInterval}. Triggers the function {@link fn} every {@link interval} milliseconds.
 * @param fn function to call
 * @param interval delay in milliseconds
 * @param run if {@link fn} should run or not
 */
export const useInterval = (fn: () => void, interval: number, run = true) => {
  const { current } = React.useRef({
    dispatch: (a: Action) => {
      current.state = update(a)(current.state);
    },
    state: paused(),
    fn,
    isMounted: false,
    run: (timeout: NodeJS.Timeout) => {
      invariant(
        State.is.running(current.state) && current.state.value === timeout && current.isMounted,
        'Expected useInterval to be in State "running"',
      );
      current.fn();
    },
  });

  current.fn = fn;

  // eslint-disable-next-line react-hooks/immutability
  React.useEffect(() => {
    current.isMounted = true;
    return () => {
      current.isMounted = false;
    };
  }, [current]);

  React.useEffect(() => {
    if (run && current.isMounted) {
      current.dispatch(Action.set([current.run, interval]));
    }
    return () => current.dispatch(Action.clear());
  }, [current, interval, run]);
};

export function useIntervalTrigger(intervalDelay: number, run = true) {
  const [dependency, update] = useForceUpdateWithDep();
  useInterval(update, intervalDelay, run);
  return dependency;
}
